import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Stamp, Users, Save } from 'lucide-react';
import api from '@/lib/api';
import { formatTL } from '@/lib/format';
import { Card, Empty } from '@/components/shared';

export default function AdminReferrals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});

  const load = () => api.get('/admin/referrals').then((r) => {
    setRows(r.data);
    const d = {};
    r.data.forEach((x) => {
      d[x.architectId] = { stamps: x.clubStamps };
      x.referrals.forEach((rf) => { d[`${x.architectId}:${rf.refereeId}`] = { bonus: rf.earnedBonus, counted: rf.countedSales }; });
    });
    setDraft(d);
  }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const saveClub = async (architectId) => {
    try {
      await api.put(`/admin/clubcard/${architectId}`, { stamps: parseInt(draft[architectId].stamps, 10) });
      toast.success('Kulüp kartı güncellendi');
      load();
    } catch { toast.error('İşlem başarısız'); }
  };

  const saveReferral = async (referrerId, refereeId) => {
    const key = `${referrerId}:${refereeId}`;
    try {
      await api.put('/admin/referrals', {
        referrerId, refereeId,
        earnedBonus: parseFloat(draft[key].bonus),
        countedSales: parseInt(draft[key].counted, 10),
      });
      toast.success('Davet primi güncellendi');
      load();
    } catch { toast.error('İşlem başarısız'); }
  };

  if (loading) return <div className="text-navy/40 text-sm">Yükleniyor...</div>;

  return (
    <div className="fade-up max-w-4xl" data-testid="admin-referrals">
      <h1 className="font-serif text-3xl text-navy mb-1">Davet & Kulüp Kartı</h1>
      <p className="text-navy/50 text-sm mb-6">Davet primlerini ve kulüp kartı durumunu görüntüle / düzelt</p>

      {rows.length === 0 ? (
        <Empty text="Davet kaydı bulunmuyor" />
      ) : (
        <div className="space-y-4" data-testid="admin-referral-list">
          {rows.map((x) => (
            <Card key={x.architectId} className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-serif text-lg text-navy">{x.name}</p>
                  <p className="text-xs text-navy/50">{x.city} · {x.referrals.length} davet · Toplam prim {formatTL(x.referralBonusTotal)}</p>
                </div>
                {/* Club card control */}
                <div className="flex items-center gap-2 bg-cream border border-navy/10 rounded-md px-3 py-2">
                  <Stamp strokeWidth={1.25} className="w-4 h-4 text-gold" />
                  <span className="text-xs text-navy/60">Damga</span>
                  <input
                    type="number"
                    value={draft[x.architectId]?.stamps ?? 0}
                    onChange={(e) => setDraft({ ...draft, [x.architectId]: { stamps: e.target.value } })}
                    data-testid={`club-stamps-${x.architectId}`}
                    className="w-14 bg-cream2 border border-navy/15 rounded px-2 py-1 text-sm text-navy focus:outline-none"
                  />
                  <button onClick={() => saveClub(x.architectId)} data-testid={`save-club-${x.architectId}`} className="text-xs bg-navy text-cream px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                    <Save strokeWidth={1.5} className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xs text-navy/50 border-l border-navy/10 pl-2 ml-1">
                    {x.completedCards} kart · {formatTL(x.clubTotalBonus)}
                  </span>
                </div>
              </div>

              {/* Referrals */}
              <div className="space-y-2">
                {x.referrals.map((rf) => {
                  const key = `${x.architectId}:${rf.refereeId}`;
                  return (
                    <div key={rf.refereeId} className="flex flex-wrap items-center justify-between gap-3 bg-cream border border-navy/10 rounded-md p-3">
                      <div className="flex items-center gap-2">
                        <Users strokeWidth={1.25} className="w-4 h-4 text-gold" />
                        <span className="text-sm text-navy font-medium">{rf.refereeName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-navy/50">Satış</label>
                        <input
                          type="number"
                          value={draft[key]?.counted ?? 0}
                          onChange={(e) => setDraft({ ...draft, [key]: { ...draft[key], counted: e.target.value } })}
                          data-testid={`ref-counted-${rf.refereeId}`}
                          className="w-14 bg-cream2 border border-navy/15 rounded px-2 py-1 text-sm focus:outline-none"
                        />
                        <label className="text-xs text-navy/50">Prim ₺</label>
                        <input
                          type="number"
                          value={draft[key]?.bonus ?? 0}
                          onChange={(e) => setDraft({ ...draft, [key]: { ...draft[key], bonus: e.target.value } })}
                          data-testid={`ref-bonus-${rf.refereeId}`}
                          className="w-24 bg-cream2 border border-navy/15 rounded px-2 py-1 text-sm focus:outline-none"
                        />
                        <button onClick={() => saveReferral(x.architectId, rf.refereeId)} data-testid={`save-ref-${rf.refereeId}`} className="text-xs bg-navy text-cream px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                          <Save strokeWidth={1.5} className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
